import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtDate } from "@/lib/format";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useEmployes, SEL } from "@/components/employes/shared";
import { Plus, Trash2, Loader2 } from "lucide-react";

const AddConge = ({ employes, onSaved }) => {
  const [v, setV] = useState({ employe_id: "", date_debut: "", date_fin: "", motif: "Congé annuel" });
  const set = (k) => (e) => setV({ ...v, [k]: e.target.value });
  const save = async () => {
    if (!v.employe_id || !v.date_debut || !v.date_fin) return toast.error("Employé et dates obligatoires");
    try {
      await api.post("/employes/conges", v);
      toast.success("Congé enregistré");
      setV({ ...v, date_debut: "", date_fin: "" });
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'enregistrement");
    }
  };
  return (
    <div className="grid gap-2 sm:grid-cols-5 items-end mb-4" data-testid="conge-form">
      <select className={SEL} value={v.employe_id} onChange={set("employe_id")} data-testid="conge-input-employe">
        <option value="">— Employé —</option>
        {employes.map((e) => <option key={e.id} value={e.id}>{e.nom} {e.prenom}</option>)}
      </select>
      <Input type="date" value={v.date_debut} onChange={set("date_debut")} data-testid="conge-input-debut" />
      <Input type="date" value={v.date_fin} onChange={set("date_fin")} data-testid="conge-input-fin" />
      <Input value={v.motif} onChange={set("motif")} placeholder="Motif" data-testid="conge-input-motif" />
      <Button className="gap-1" onClick={save} data-testid="conge-save"><Plus className="h-4 w-4" /> Ajouter</Button>
    </div>
  );
};

const Soldes = ({ employes }) => (
  <table className="w-full text-sm min-w-[600px]" data-testid="conges-soldes">
    <thead><tr className="text-left text-muted-foreground border-b">{["Employé", "Société", "Droits annuels", "Pris", "Reste"].map((h) => <th key={h} className="py-2 pr-2 font-medium">{h}</th>)}</tr></thead>
    <tbody className="divide-y">{employes.map((e) => (
      <tr key={e.id} data-testid={`solde-row-${e.id}`}>
        <td className="py-2 pr-2 font-medium">{e.nom} {e.prenom}</td><td className="py-2 pr-2 text-xs">{e.societe_nom}</td>
        <td className="py-2 pr-2">{e.conges_annuels} j</td><td className="py-2 pr-2">{e.conges_pris} j</td>
        <td className={`py-2 pr-2 font-semibold ${e.conges_reste < 0 ? "text-red-600" : "text-emerald-700"}`}>{e.conges_reste} j</td>
      </tr>))}</tbody>
  </table>
);

export default function EmployeConges() {
  const [annee, setAnnee] = useState(new Date().getFullYear());
  const { items: employes, load: loadE, editable, q } = useEmployes(annee);
  const [conges, setConges] = useState(null);
  const load = () => api.get(`/employes/conges/list?${q}`).then((r) => setConges(r.data)).catch(() => setConges([]));
  useEffect(() => { load(); }, [q]); // eslint-disable-line
  const refresh = () => { load(); loadE(); };
  const onDelete = async (c) => { if (window.confirm("Supprimer ce congé ?")) { await api.delete(`/employes/conges/${c.id}`); refresh(); } };
  const years = Array.from({ length: 4 }, (_, i) => new Date().getFullYear() + 1 - i);

  return (
    <div data-testid="conges-page">
      <PageHeader title="États des congés" subtitle="Droits, congés pris et reste par employé"
        actions={<select className={`${SEL} w-28`} value={annee} onChange={(e) => setAnnee(Number(e.target.value))} data-testid="conges-annee">{years.map((y) => <option key={y}>{y}</option>)}</select>} />
      {employes === null || conges === null ? <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div> : <>
        <div className="rounded-xl border bg-card p-5 mb-5 overflow-x-auto"><h3 className="font-heading font-bold mb-3">Soldes {annee}</h3>
          {employes.length ? <Soldes employes={employes} /> : <p className="text-sm text-muted-foreground">Créez d'abord vos employés dans « Dates d'embauche ».</p>}</div>
        <div className="rounded-xl border bg-card p-5 overflow-x-auto"><h3 className="font-heading font-bold mb-3">Congés pris {annee}</h3>
          {editable && employes.length > 0 && <AddConge employes={employes} onSaved={refresh} />}
          <table className="w-full text-sm min-w-[600px]"><thead><tr className="text-left text-muted-foreground border-b">{["Employé", "Du", "Au", "Jours", "Motif"].map((h) => <th key={h} className="py-2 pr-2 font-medium">{h}</th>)}<th /></tr></thead>
            <tbody className="divide-y">{conges.map((c) => (
              <tr key={c.id} data-testid={`conge-row-${c.id}`}><td className="py-2 pr-2 font-medium">{c.employe_nom}</td><td className="py-2 pr-2">{fmtDate(c.date_debut)}</td>
                <td className="py-2 pr-2">{fmtDate(c.date_fin)}</td><td className="py-2 pr-2">{c.jours}</td><td className="py-2 pr-2">{c.motif}</td>
                <td className="py-2 text-right">{editable && <Button size="sm" variant="ghost" onClick={() => onDelete(c)} data-testid={`conge-delete-${c.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>}</td></tr>))}</tbody>
          </table>
          {!conges.length && <p className="text-sm text-muted-foreground py-3">Aucun congé enregistré pour {annee}.</p>}
        </div>
      </>}
    </div>
  );
}
